// FF-1129 (Analytics Outbound v1 — app GA4 light, ZEE port)
//
// Builds the backend request body for POST /v1/app-analytics/event.
//
// The client only builds the lightweight body the backend needs:
//
//   { client_id, event_name, params }
//
// The server (ff-backend, Clerk-authed) stamps user_id, org_id (→ brand_id),
// and forwards to GA4 MP using credentials it holds server-side.
//
// Design constraints:
//  - Pure: no network, no DOM, no global side-effects.
//  - client_id is still minted here (stable random string) and persisted in
//    localStorage by app-analytics.ts so GA4 can stitch sessions server-side.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Body sent to POST /v1/app-analytics/event. */
export interface BackendEventBody {
  client_id: string;
  event_name: string;
  params: Record<string, unknown>;
}

/** Input to buildBackendEventBody. */
export interface BuildBackendEventBodyInput {
  clientId: string;
  eventName: string;
  params?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate a random GA4-style client_id.
 *
 * Format: <random_int>.<unix_ts_seconds>
 * The instance layer (app-analytics.ts) persists this in localStorage so the
 * same browser sends all events with one cid.
 */
export const generateClientId = (): string => {
  const rand = Math.floor(Math.random() * 1_000_000_000);
  const ts = Math.floor(Date.now() / 1000);
  return `${rand}.${ts}`;
};

/**
 * Build the request body for POST /v1/app-analytics/event.
 *
 * Does NOT include user_id, org_id, brand_id, measurement_id, or api_secret —
 * those are stamped server-side from the Clerk token and stored credentials.
 */
export const buildBackendEventBody = (
  input: BuildBackendEventBodyInput
): BackendEventBody => ({
  client_id: input.clientId,
  event_name: input.eventName,
  params: { ...(input.params ?? {}) },
});
